/**
 * COL-417: the receipts behind a payment row.
 *
 * `payments.received_cents` answers "how much arrived"; these rows answer "when,
 * how, and who recorded it" — which is what an admin needs to correct a
 * mis-keyed figure, and what the audit trail is made of.
 *
 * Writes go straight to the table under the admin RLS policy, the same way the
 * rest of the tenant profile writes `payments` and `payment_proofs` directly.
 * Everything that FOLLOWS from a receipt is the database's job, not this hook's:
 * `sync_payment_from_receipts` recomputes received_cents and the row's status,
 * and `log_payment_receipt_activity` writes the tenant timeline entry. That is
 * deliberate — the same receipts are created by
 * `confirm_reconciliation_match`, and a browser-side status update or activity
 * insert would simply not happen for those.
 *
 * Query keys live under ["v2", ...] so the tenant profile's `refreshAfterWrite`
 * (which invalidates the whole "v2" tree) picks up receipts along with
 * everything else it already refreshes.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert } from "@/lib/supabase-types";
import { invalidateOccupancyViews } from "@/hooks/v2/useProperties";

export type ReceiptAppliesTo = "rent" | "fee";

export type PaymentReceipt = {
  id: string;
  payment_id: string;
  amount_cents: number;
  applies_to: ReceiptAppliesTo;
  received_at: string;
  method: string | null;
  source: string;
  bank_transaction_id: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
};

export const PAYMENT_RECEIPT_KEYS = {
  forBooking: (bookingId: number | null) =>
    ["v2", "payment-receipts", bookingId] as const,
};

/**
 * Every receipt for a booking, grouped by payment id, newest first.
 *
 * Grouped here rather than at each call site so the rent row, the history card
 * and any future surface all read the same shape — and so "newest first" is
 * decided once (the row lists receipts in the order an admin would read them
 * back: most recent correction at the top).
 */
export function usePaymentReceiptsByBooking(bookingId: number | null) {
  return useQuery<Record<string, PaymentReceipt[]>>({
    queryKey: PAYMENT_RECEIPT_KEYS.forBooking(bookingId),
    enabled: bookingId != null,
    queryFn: async () => {
      if (bookingId == null) return {};
      // Scoped through the booking's payments rather than by a receipt column,
      // because a receipt has no booking_id of its own — the payment owns that
      // relationship, and duplicating it would be another copy to keep in sync.
      const { data: paymentRows, error: payErr } = await supabase
        .from("payments")
        .select("id")
        .eq("booking_id", bookingId);
      if (payErr) throw payErr;

      const ids = (paymentRows ?? []).map((p: { id: string }) => String(p.id));
      if (ids.length === 0) return {};

      const { data, error } = await supabase
        .from("payment_receipts")
        .select("*")
        .in("payment_id", ids)
        .order("received_at", { ascending: false });
      if (error) throw error;

      const grouped: Record<string, PaymentReceipt[]> = {};
      for (const row of (data ?? []) as PaymentReceipt[]) {
        (grouped[String(row.payment_id)] ??= []).push(row);
      }
      return grouped;
    },
  });
}

export type RecordPaymentInput = {
  paymentId: string;
  /** Cents applied to the rent balance. Zero for a fee-only payment. */
  rentCents: number;
  /** Cents recorded against accrued Mahngebühren. Zero when there are none. */
  feeCents: number;
  /** ISO date the money actually arrived (not when it was typed in). */
  receivedAt: string;
  method: string | null;
  note: string | null;
};

/**
 * Record one payment as up to two receipts — the rent part and the fee part.
 *
 * Both rows are written in a single `insert` call so one transfer cannot land
 * half-recorded: if the fee row were a second round-trip and it failed, the
 * month would silently show more rent received than the tenant actually paid
 * toward it.
 */
export function useRecordPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecordPaymentInput) => {
      // Resolved here rather than passed in by every caller: `created_by` is
      // the record of WHO said the money arrived, which is the same thing
      // marked_paid_at/by mean elsewhere, so it must not depend on a component
      // remembering to thread it through.
      const { data: auth } = await supabase.auth.getUser();
      const createdBy = auth?.user?.id ?? null;

      const rows: TablesInsert<"payment_receipts">[] = [];
      const base = {
        payment_id: input.paymentId,
        received_at: input.receivedAt,
        method: input.method,
        source: "admin",
        note: input.note,
        created_by: createdBy,
      };
      if (input.rentCents > 0)
        rows.push({
          ...base,
          amount_cents: input.rentCents,
          applies_to: "rent",
        });
      if (input.feeCents > 0)
        rows.push({ ...base, amount_cents: input.feeCents, applies_to: "fee" });
      if (rows.length === 0) throw new Error("Nothing to record.");

      const { error } = await supabase.from("payment_receipts").insert(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["v2"] });
      void invalidateOccupancyViews(queryClient);
    },
  });
}

/** Correct a mis-keyed receipt. The trigger restates the payment from scratch. */
export function useUpdateReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      amountCents?: number;
      receivedAt?: string;
      method?: string | null;
      note?: string | null;
    }) => {
      const patch: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };
      if (input.amountCents != null) patch.amount_cents = input.amountCents;
      if (input.receivedAt != null) patch.received_at = input.receivedAt;
      if (input.method !== undefined) patch.method = input.method;
      if (input.note !== undefined) patch.note = input.note;

      const { error } = await supabase
        .from("payment_receipts")
        .update(patch)
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["v2"] });
      void invalidateOccupancyViews(queryClient);
    },
  });
}

/**
 * Remove a receipt entered in error.
 *
 * The month reopens on its own: the trigger recomputes received_cents, drops
 * the row back to partially_paid (or pending), and
 * recompute_booking_rent_coverage retracts rent_paid_through if the deletion
 * un-settles a month that had closed.
 */
export function useDeleteReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("payment_receipts")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["v2"] });
      void invalidateOccupancyViews(queryClient);
    },
  });
}

export type ReceiptBankTransaction = {
  id: string;
  finapi_transaction_id: number | null;
  amount_cents: number;
  currency: string | null;
  counterpart_name: string | null;
  counterpart_iban: string | null;
  /** Verwendungszweck. */
  purpose: string | null;
  booking_date: string | null;
  value_date: string | null;
};

export const RECEIPT_BANK_TX_KEYS = {
  forReceipts: (ids: readonly string[]) =>
    ["v2", "receipt-bank-transactions", [...ids].sort().join(",")] as const,
};

/**
 * COL-417 follow-up: the bank transactions behind reconciliation receipts.
 *
 * A receipt written by `confirm_reconciliation_match` deliberately does NOT
 * get an approved `payment_proofs` row — attaching one would mark the month
 * settled, which is exactly the amount-aware bug COL-417 fixed. The evidence
 * still exists, it is just the bank's own record rather than an uploaded
 * document: who sent it, from which IBAN, with what Verwendungszweck, booked
 * on which day. Without this the strongest proof in the system was the one
 * nothing surfaced.
 *
 * Keyed on the id set so two rows sharing a transaction share one fetch, and
 * disabled entirely when no receipt came from reconciliation.
 */
export function useReceiptBankTransactions(
  bankTransactionIds: readonly string[]
) {
  const ids = [...new Set(bankTransactionIds.filter(Boolean))];
  return useQuery<Record<string, ReceiptBankTransaction>>({
    queryKey: RECEIPT_BANK_TX_KEYS.forReceipts(ids),
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bank_transactions")
        .select(
          "id, finapi_transaction_id, amount_cents, currency, counterpart_name, counterpart_iban, purpose, booking_date, value_date"
        )
        .in("id", ids);
      if (error) throw error;
      const out: Record<string, ReceiptBankTransaction> = {};
      for (const tx of (data ?? []) as ReceiptBankTransaction[])
        out[String(tx.id)] = tx;
      return out;
    },
  });
}
